import { Box, CircularProgress, Theme } from '@mui/material'
import { useTheme } from '@mui/styles'
import useUserRole from 'hooks/useUserRole'
import PerfectScrollbar from 'perfect-scrollbar'
import 'perfect-scrollbar/css/perfect-scrollbar.css'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'
import { StateResourceType } from '../../app/constants/Dashboard/DashboardConstants'
import AdminNavbar from '../../materialUI/components/Navbars/AdminNavbar'
import Sidebar from '../../materialUI/components/Sidebar/Sidebar'
import {
  cAStateRoutes,
  useClientAdminRoutes,
} from '../../routes/DashboardRoutes/clientAdminRoutes'
import {
  jAStateRoutes,
  useJyrneyAdminRoutes,
} from '../../routes/DashboardRoutes/jyrneyAdminRoutes'
import { useStandardUserRoutes } from '../../routes/DashboardRoutes/standardUserRoutes'
import styles from './adminStyle'

export function Admin(props: any) {
  const theme = useTheme<Theme>()
  const { validateAccess } = useUserRole()
  const { ...rest } = props

  const currentUser = useSelector((state: any) => state.users.user)
  const currentClient = useSelector((state: any) => state.clients.client)
  const [mainPanel, setMainPanel] = useState<HTMLElement | null>(null)
  const [routes, setRoutes] = useState<any>([])
  // states and functions
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [miniActive, setMiniActive] = React.useState(false)
  const [color] = React.useState('custom')
  const [bgColor] = React.useState('black')
  const [redirectPath, setRedirectPath] = React.useState(
    '/admin/bookings/create?new=true',
  )

  const updateStateRoutes = useCallback(
    (routes: any, stateRoutes: any) => {
      const updatedRoutes = routes.map((item: any) => {
        if (item.stateRouteId !== undefined) {
          const routeToUpdate = stateRoutes.find(
            (state: any) => state.stateRouteId === item.stateRouteId,
          )

          if (routeToUpdate !== null) {
            switch (routeToUpdate.stateSource) {
              case StateResourceType.CLIENT:
                if (routeToUpdate.stateField === 'clientId') {
                  const newRoute = {
                    ...item,
                  }
                  newRoute.path = `${newRoute.path}${currentClient.clientId}`
                  newRoute.name =
                    currentClient.companyName.length < 16
                      ? currentClient.companyName
                      : `${currentClient.companyName.substring(0, 16)}...`
                  return newRoute
                }
                break
              default:
                return item
            }
          } else {
            return item
          }
        } else {
          return item
        }
      })

      return updatedRoutes
    },
    [currentClient?.companyName, currentClient?.clientId],
  )

  React.useEffect(() => {
    if (currentUser && currentClient) {
      let userRoutes = []

      const allRoutes = updateStateRoutes(
        useJyrneyAdminRoutes(currentUser, currentClient),
        jAStateRoutes,
      )

      if (validateAccess('JYRNEY_ADMIN')) {
        if (jAStateRoutes.length > 0) {
          userRoutes = updateStateRoutes(
            useJyrneyAdminRoutes(currentUser, currentClient),
            jAStateRoutes,
          )
        } else {
          userRoutes = useJyrneyAdminRoutes(currentUser, currentClient)
        }
      } else if (validateAccess('CLIENT_ADMIN')) {
        if (cAStateRoutes.length > 0) {
          userRoutes = updateStateRoutes(
            useClientAdminRoutes(currentUser, currentClient),
            cAStateRoutes,
          )
        } else {
          userRoutes = useClientAdminRoutes(currentUser, currentClient)
        }
      } else if (validateAccess('CLIENT_SUPPORT')) {
        setRedirectPath('/admin/clients/create')
        userRoutes = allRoutes
          .filter((x: any) => x.name !== 'Suppliers')
          .map((v: any) => {
            if (v.name === 'Bookings') {
              v.views = v.views.filter((x: any) =>
                x.path.includes('/bookings/journeyStatus'),
              )
            }
            return v
          })
      } else if (validateAccess('SUPPLIER_SUPPORT')) {
        setRedirectPath('/admin/supplier/create/company')
        userRoutes = useJyrneyAdminRoutes(currentUser, currentClient).filter(
          (x) => x.name === 'Suppliers',
        )
      } else if (
        validateAccess('OPERATIONS_SUPPORT') ||
        validateAccess('OPERATIONS_SUPPORT_SUPERVISOR') ||
        validateAccess('TRAVEL_CONSULTANT') ||
        validateAccess('SENIOR_TRAVEL_CONSULTANT')
      ) {
        userRoutes = allRoutes.filter(
          (x: any) =>
            !(x.path && x.path.includes('/clients')) &&
            !['Clients', 'Suppliers'].includes(x.name),
        )
      } else {
        // Finance, Traveller
        userRoutes = useStandardUserRoutes(currentUser, currentClient)
      }
      setRoutes(userRoutes)
    }
  }, [currentClient, currentUser, updateStateRoutes])

  // ref for main panel div
  const mainPanelRef: any = useCallback((node: HTMLElement) => {
    setMainPanel(node)
  }, [])

  useEffect(() => {
    var ps: any
    if (mainPanel != null && navigator.platform.indexOf('Win') > -1) {
      ps = new PerfectScrollbar(mainPanel, {
        suppressScrollX: true,
        suppressScrollY: false,
      })
      document.body.style.overflow = 'hidden'
    }

    return function cleanup() {
      if (ps != null && navigator.platform.indexOf('Win') > -1) {
        ps.destroy()
      }
    }
  }, [mainPanel])

  const resizeFunction = () => {
    if (window.innerWidth >= 960) {
      setMobileOpen(false)
    }
  }

  // effect instead of componentDidMount, componentDidUpdate and componentWillUnmount
  React.useEffect(() => {
    window.addEventListener('resize', resizeFunction)

    // Specify how to clean up after this effect:
    return function cleanup() {
      window.removeEventListener('resize', resizeFunction)
    }
  }, [])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const getActiveRoute = (routes: any) => {
    let activeRoute = 'ADMINISTRATION'

    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute: any = getActiveRoute(routes[i].views)
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].navBarHeading
        }
      }
    }
    return activeRoute
  }
  const getRoutes = (routes: any) => {
    return routes.map((prop: any, key: any) => {
      if (prop.stateRouteId !== undefined) {
        return null
      }

      if (prop.collapse) {
        return getRoutes(prop.views)
      }
      if (prop.layout === '/admin') {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        )
      }
      return null
    })
  }
  const sidebarMinimize = () => {
    setMiniActive(!miniActive)
  }

  if (routes.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="var(--app-vh)"
      >
        <CircularProgress color="primary" />
      </Box>
    )
  }

  return (
    <Box component={'div'} sx={styles.wrapper}>
      <Sidebar
        routes={routes}
        logoText={''}
        logo={theme.brand.logo}
        logoNoPadding={theme.brand.sidebarLogoNoPadding}
        image={theme.brand.sidebarImage}
        handleDrawerToggle={handleDrawerToggle}
        open={mobileOpen}
        color={color}
        bgColor={bgColor}
        miniActive={miniActive}
        {...rest}
      />

      <Box
        component={'div'}
        id="jyrney-booking-wrapper"
        sx={[
          styles.mainPanel,
          miniActive && styles.mainPanelSidebarMini,
          navigator.platform.indexOf('Win') > -1 &&
            styles.mainPanelWithPerfectScrollbar,
        ]}
        ref={mainPanelRef}
      >
        <AdminNavbar
          //@ts-ignore
          sidebarMinimize={sidebarMinimize.bind(this)}
          miniActive={miniActive}
          brandText={getActiveRoute(routes)}
          handleDrawerToggle={handleDrawerToggle}
          {...rest}
        />

        <Switch>
          {getRoutes(routes)}
          <Redirect from="/admin" to={redirectPath} />
          <Redirect from="/admin/dashboard" to={redirectPath} />
        </Switch>
      </Box>
    </Box>
  )
}

export default Admin
